import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  input,
  OnInit,
  output,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { ControlsOf, FormBuilder, FormGroup } from '@ngneat/reactive-forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { InputType, ModalRef, ToastV2Service } from '@vinnova/ui';
import {
  ValidateBG,
  ValidateBSB,
  ValidateFW,
  ValidateIban,
  ValidatePG,
  ValidateRouting,
  ValidateSwift,
} from '@vinnova/validation';
import * as i18nIsoCountries from 'i18n-iso-countries';
import { BehaviorSubject, debounceTime, filter, switchMap, tap } from 'rxjs';
import { AccountsCommand } from '../../../core/commands/accounts.command';
import { Account, AccountType } from '../../../core/models/account.model';
import { Country } from '../../../core/models/country.model';
import { VinnovaStatus } from '../../../core/models/vinnova-status.model';
import { AccountsQuery } from '../../../core/queries/accounts.query';
import { AccountService } from '../../../core/services/account-service';

@UntilDestroy()
@Component({
  selector: 'lib-account-form',
  templateUrl: './account-form.component.html',
  styleUrls: ['./account-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AccountFormComponent implements OnInit {
  accountToEdit = input<Account>();
  worksiteId = input<string>();
  companyId = input<string>();
  accountReadonly = input<boolean>(false);

  // eslint-disable-next-line @angular-eslint/no-output-native
  cancel = output<void>();
  accountSubmitted = output<Account>();
  accountToAttestera = output<Account>();

  accountForm: FormGroup<ControlsOf<Account>>;
  accountSameCompany: Account;
  accountOtherCompany: boolean;
  accountOtherCompanyName: string;
  isSearching: boolean;
  buttonWait: boolean;

  AccountType = AccountType;
  InputType = InputType;

  accountType;
  accountTypeItems = [
    { value: 'BankGiro', label: undefined },
    { value: 'PlusGiro', label: undefined },
    { value: 'AU', label: undefined },
    { value: 'CA', label: undefined },
    { value: 'US', label: undefined },
    {
      value: 'OTHER',
      label: undefined,
    },
  ];

  accountTypeLabels = [
    { labelSv: 'Bankgiro', labelEn: 'Bank transfer number' },
    { labelSv: 'PlusGiro', labelEn: 'PlusGiro' },
    {
      labelSv: 'Utlandskonto från Australien',
      labelEn: 'Account from Australia',
    },
    { labelSv: 'Utlandskonto från Kanada', labelEn: 'Account from Canada' },
    { labelSv: 'Utlandskonto från USA', labelEn: 'Account from USA' },
    {
      labelSv: 'Utlandskonto (alla utom Australien, Kanada, USA)',
      labelEn: 'Foreign account (all except Australia, Canada, USA)',
    },
  ];

  countries: Country[];
  // LIST FROM https://www.iban.com/structure
  ibanCountries = [
    'AL',
    'AD',
    'AT',
    'AZ',
    'BH',
    'BY',
    'BE',
    'BA',
    'BR',
    'BG',
    'BI',
    'CR',
    'HR',
    'CY',
    'CZ',
    'DK',
    'DJ',
    'DO',
    'EG',
    'SV',
    'EE',
    'FK',
    'FO',
    'FI',
    'FR',
    'GE',
    'DE',
    'GI',
    'GR',
    'GL',
    'GT',
    'VA',
    'HU',
    'IS',
    'IQ',
    'IE',
    'IL',
    'IT',
    'JO',
    'KZ',
    'XK',
    'KW',
    'LV',
    'LB',
    'LY',
    'LI',
    'LT',
    'LU',
    'MT',
    'MR',
    'MU',
    'MD',
    'MC',
    'MN',
    'ME',
    'NL',
    'NI',
    'MK',
    'NO',
    'PK',
    'PS',
    'PL',
    'PT',
    'QA',
    'RO',
    'RU',
    'LC',
    'SM',
    'ST',
    'SA',
    'RS',
    'SC',
    'SK',
    'SI',
    'SO',
    'ES',
    'SD',
    //'SE', //DON'T SHOW SWEDEN IN LIST
    'CH',
    'TL',
    'TN',
    'TR',
    'UA',
    'AE',
    'GB',
    'VG',
    //FOLLOWING ARE PARTIAL (EXPERIMENTAL)
    'DZ',
    'AO',
    'BJ',
    'BF',
    'CM',
    'CV',
    'CF',
    'TD',
    'KM',
    'CG',
    'GQ',
    'GA',
    'GW',
    'HN',
    'IR',
    'CI',
    'MG',
    'ML',
    'MA',
    'MZ',
    'NE',
    'SN',
    'TG',
  ];

  vinnovaStatusItems = [
    { value: 0, label: 'Overifierad', disabled: false },
    { value: 1, label: 'Verifierad', disabled: false },
    { value: 2, label: 'Spärrad', disabled: false },
    { value: 3, label: 'Obsolet', disabled: false },
  ];

  selectedCountry: string;

  confirmAttesteraModal: ModalRef;
  attesteringInProgress = false;
  attestering$ = new BehaviorSubject(false);

  addAccountDisabled = true;

  constructor(
    private fb: FormBuilder,
    private accountsCommand: AccountsCommand,
    private accountsQuery: AccountsQuery,
    private cdr: ChangeDetectorRef,
    private translocoService: TranslocoService,
    private toastService: ToastV2Service,
    private accountService: AccountService,
  ) {}

  ngOnInit(): void {
    this.createForm();

    for (const [index, type] of this.accountTypeItems.entries()) {
      type.label =
        this.translocoService.getActiveLang() === 'en'
          ? this.accountTypeLabels[index].labelEn
          : this.accountTypeLabels[index].labelSv;
    }

    this.translocoService.langChanges$
      .pipe(untilDestroyed(this))
      .subscribe((lang) => {
        for (const [index, type] of this.accountTypeItems.entries()) {
          type.label =
            lang === 'en'
              ? this.accountTypeLabels[index].labelEn
              : this.accountTypeLabels[index].labelSv;
        }
      });

    const countriesObject = i18nIsoCountries.getNames('sv');
    const countries = new Array<Country>();
    for (const key of Object.keys(countriesObject)) {
      countries.push({
        value: key,
        label: countriesObject[key],
      } as Country);
    }
    // eslint-disable-next-line sonarjs/no-misleading-array-reverse -- countries is freshly built above, not referenced elsewhere
    const sortedCountries = countries.sort((a, b) => {
      if (a.label > b.label) {
        return 1;
      }
      if (a.label < b.label) {
        return -1;
      }
      return 0;
    });
    this.countries = sortedCountries.filter((c) =>
      this.ibanCountries.includes(c.value),
    );
    this.attestering$.pipe(untilDestroyed(this)).subscribe((a) => {
      if (a) {
        this.accountToAttestera.emit(this.accountToEdit());
      }
    });

    this.accountForm.valid$.pipe(untilDestroyed(this)).subscribe((e) => {
      this.addAccountDisabled = !e;
    });
  }

  submitForm() {
    if (this.accountForm.invalid) {
      this.accountForm.markAllAsTouched();
      this.accountForm.markAllAsDirty();
      return;
    }

    if (this.isSearching) {
      this.buttonWait = true;
      return;
    }

    if (this.attesteringInProgress) {
      return;
    }
    const submitCommand = this.accountToEdit()
      ? this.accountsCommand.updateAccount(this.accountForm.value)
      : this.accountsCommand.addAccount(this.accountForm.value);
    submitCommand.pipe(untilDestroyed(this)).subscribe({
      next: (account) => {
        if (!this.accountToEdit()) {
          this.accountService.setNewAccount(account.id);
        }
        this.accountSubmitted.emit(account);
      },
      error: (error) => {
        this.accountSubmitted.emit(undefined);
        this.toastService.error(error.error);
      },
    });
  }

  private createForm() {
    this.accountForm = this.fb.group({
      id: this.fb.control<string>(
        this.accountToEdit() ? this.accountToEdit().id : undefined,
      ),
      worksiteId: this.fb.control<string>(this.worksiteId()),
      companyId: this.fb.control<string>(this.companyId() ?? undefined),
      number: this.fb.control<string>(
        this.accountToEdit() ? this.accountToEdit().number : undefined,
        [Validators.required],
      ),
      swift: this.fb.control<string>(
        this.accountToEdit() ? this.accountToEdit().swift : undefined,
        [Validators.required, ValidateSwift],
      ),
      iban: this.fb.control<string>(
        this.accountToEdit() ? this.accountToEdit().iban : undefined,
        [Validators.required],
      ),
      bsbCode: this.fb.control<string>(
        this.accountToEdit() ? this.accountToEdit().bsbCode : undefined,
        [Validators.required, ValidateBSB],
      ),
      fw: this.fb.control<string>(
        this.accountToEdit() ? this.accountToEdit().fw : undefined,
        [Validators.required, ValidateFW],
      ),
      routingNumber: this.fb.control<string>(
        this.accountToEdit() ? this.accountToEdit().routingNumber : undefined,
        [Validators.required, ValidateRouting],
      ),
      type: this.fb.control<AccountType>(
        this.accountToEdit() ? this.accountToEdit().type : undefined,
        [Validators.required],
      ),
      country: this.fb.control<string>(
        this.accountToEdit() ? this.accountToEdit().country : undefined,
        [Validators.required],
      ),
      vinnovaStatus: this.fb.control<VinnovaStatus>(
        this.accountToEdit() ? this.accountToEdit().vinnovaStatus : undefined,
      ),
    });
    this.selectedCountry = this.accountForm.value.country;
    this.accountForm
      .get('country')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((val) => this.onCountryChange(val));

    this.vinnovaStatusItems = this.vinnovaStatusItems.map((v) => {
      if (
        (this.accountToEdit() &&
          this.accountToEdit().vinnovaStatus !== VinnovaStatus.Verified &&
          v.value === VinnovaStatus.Verified) ||
        v.value === VinnovaStatus.Unverified
      ) {
        v.disabled = true;
      }
      return v;
    });
    this.accountForm
      .get('number')
      .valueChanges.pipe(
        debounceTime(500),
        tap(() => {
          this.accountSameCompany = undefined;
          this.accountOtherCompany = false;
          this.accountOtherCompanyName = undefined;
        }),
        filter(
          (val) =>
            this.accountForm.valid &&
            this.accountForm.value.country === 'SE' &&
            (!this.accountToEdit() || this.accountToEdit().number !== val),
        ),
        tap(() => {
          this.isSearching = true;
        }),
        switchMap((val) =>
          this.accountsQuery.searchAccount(
            val,
            this.worksiteId() ?? undefined,
            this.companyId() ?? undefined,
          ),
        ),
        untilDestroyed(this),
      )
      .subscribe({
        next: (result) => {
          this.isSearching = false;
          if (result) {
            if (result.existOnSameCompany) {
              this.accountSameCompany = result;
            } else {
              this.accountOtherCompany = true;
              this.accountOtherCompanyName = result.companyName;
            }
          } else {
            if (this.buttonWait) {
              this.submitForm();
            }
          }
          this.buttonWait = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.isSearching = false;
        },
      });

    this.disableAllFields();
    if (this.accountToEdit()) {
      if (
        (this.accountToEdit().type as unknown as string) === 'BankGiro' ||
        (this.accountToEdit().type as unknown as string) === 'PlusGiro'
      ) {
        this.accountType =
          (this.accountToEdit().type as unknown as string) === 'BankGiro'
            ? 'BankGiro'
            : 'PlusGiro';
      } else if (['AU', 'CA', 'US'].includes(this.accountToEdit().country)) {
        this.accountType = this.accountToEdit().country;
      } else {
        this.accountType = 'OTHER';
      }
      this.onAccountTypeChange(this.accountType);
    }
  }

  private disableAllFields() {
    this.accountForm.get('number').disable();
    this.accountForm.get('swift').disable();
    this.accountForm.get('iban').disable();
    this.accountForm.get('bsbCode').disable();
    this.accountForm.get('fw').disable();
    this.accountForm.get('routingNumber').disable();
    this.accountForm.get('vinnovaStatus').disable();
  }

  public onAccountTypeChange(accountType: string) {
    this.disableAllFields();
    this.accountSameCompany = undefined;
    this.accountOtherCompany = false;
    this.accountOtherCompanyName = undefined;
    switch (accountType) {
      case 'BankGiro': {
        this.accountForm.get('number').enable();
        this.accountForm
          .get('type')
          .patchValue(AccountType.BankGiro, { emitEvent: false });
        this.accountForm.get('country').patchValue('SE', { emitEvent: false });
        this.accountForm
          .get('number')
          .setValidators([Validators.required, ValidateBG]);
        break;
      }
      case 'PlusGiro': {
        this.accountForm.get('number').enable();
        this.accountForm
          .get('type')
          .patchValue(AccountType.PlusGiro, { emitEvent: false });
        this.accountForm.get('country').patchValue('SE', { emitEvent: false });
        this.accountForm
          .get('number')
          .setValidators([Validators.required, ValidatePG]);
        break;
      }
      case 'AU': {
        this.accountForm.get('number').enable();
        this.accountForm.get('number').setValidators(Validators.required);
        this.accountForm.get('swift').enable();
        this.accountForm.get('bsbCode').enable();
        this.accountForm
          .get('type')
          .patchValue(AccountType.Utlandskonto, { emitEvent: false });
        this.accountForm.get('country').patchValue('AU', { emitEvent: false });
        break;
      }
      case 'CA': {
        this.accountForm.get('number').enable();
        this.accountForm.get('number').setValidators(Validators.required);
        this.accountForm.get('swift').enable();
        this.accountForm.get('routingNumber').enable();
        this.accountForm
          .get('type')
          .patchValue(AccountType.Utlandskonto, { emitEvent: false });
        this.accountForm.get('country').patchValue('CA', { emitEvent: false });
        break;
      }
      case 'US': {
        this.accountForm.get('number').enable();
        this.accountForm.get('number').setValidators(Validators.required);
        this.accountForm.get('swift').enable();
        this.accountForm.get('fw').enable();
        this.accountForm
          .get('type')
          .patchValue(AccountType.Utlandskonto, { emitEvent: false });
        this.accountForm.get('country').patchValue('US', { emitEvent: false });
        break;
      }
      case 'OTHER': {
        this.accountForm.get('swift').enable();
        this.accountForm.get('iban').enable();
        this.accountForm
          .get('type')
          .patchValue(AccountType.Utlandskonto, { emitEvent: false });
        this.accountForm
          .get('country')
          .patchValue(this.selectedCountry, { emitEvent: false });

        this.accountForm
          .get('iban')
          .setValidators([Validators.required, ValidateIban()]);
        break;
      }
    }
  }

  private onCountryChange(countryCode) {
    if (this.accountType === 'OTHER') {
      this.accountForm
        .get('iban')
        .setValidators([Validators.required, ValidateIban(countryCode)]);
      this.selectedCountry = countryCode;
    }
  }
}
